import React, { useState, useEffect, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { AnimatePresence } from 'framer-motion';
import { ManualRevenueEntry } from '../../types/Dashboard';
import dashboardService from '../../services/dashboardService';
import { useAuth } from '../../contexts/AuthContext';
import { today, formatDateAR } from './dashboardHelpers';
import { formatCurrency } from './dashboardUi';
import { Alert, Badge, Button, cn } from '../ui';
import { CheckIcon, CloseIcon, PencilIcon, TrashIcon } from '../icons';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  planName: string;
  initialHistory: ManualRevenueEntry[];
}

const fieldLabel = 'mb-1.5 block text-xs font-semibold text-slate-600';

const field =
  'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 ' +
  'transition-colors duration-150 ease-out outline-none ' +
  'focus:border-gappex-primary focus:ring-2 focus:ring-gappex-primary/30';

const EditRevenueModal: React.FC<Props> = ({ isOpen, onClose, onSaved, planName, initialHistory }) => {
  const { userRole } = useAuth();

  const [editingId, setEditingId] = useState<number | null>(null);
  /** Id con borrado pendiente de confirmar, en la fila misma. */
  const [confirmingId, setConfirmingId] = useState<number | null>(null);
  const [date, setDate] = useState(today());
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<ManualRevenueEntry[]>(initialHistory);

  useEffect(() => {
    setHistory(initialHistory);
  }, [initialHistory]);

  if (userRole !== 'admin' && userRole !== 'manager') return null;

  const isEditing = editingId !== null;

  const resetForm = () => {
    setEditingId(null);
    setDate(today());
    setAmount('');
    setNotes('');
    setError(null);
  };

  const handleEdit = (entry: ManualRevenueEntry) => {
    setEditingId(entry.id);
    setConfirmingId(null);
    setDate(entry.effective_date);
    setAmount(String(entry.monthly_amount));
    setNotes(entry.notes ?? '');
    setError(null);
  };

  const handleDelete = async (entry: ManualRevenueEntry) => {
    setConfirmingId(null);
    const result = await dashboardService.deleteManualRevenue(entry.id);
    if (result.success) {
      const fresh = await dashboardService.getManualRevenueHistory(planName);
      setHistory(fresh);
      if (editingId === entry.id) resetForm();
      onSaved();
    } else {
      setError(result.message ?? 'No se pudo eliminar el ajuste.');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const parsed = parseFloat(amount);
    if (isNaN(parsed) || parsed < 0) {
      setError('Ingresá un monto válido.');
      return;
    }
    setSaving(true);

    let result;
    if (!isEditing) {
      result = await dashboardService.createManualRevenue({
        planName,
        monthlyAmount: parsed,
        effectiveDate: date,
        notes: notes.trim() || undefined,
      });
    } else {
      result = await dashboardService.updateManualRevenue(editingId, {
        monthlyAmount: parsed,
        effectiveDate: date,
        notes: notes.trim() || undefined,
      });
    }

    setSaving(false);
    if (result.success) {
      const fresh = await dashboardService.getManualRevenueHistory(planName);
      setHistory(fresh);
      resetForm();
      onSaved();
    } else {
      setError(result.message ?? 'No se pudo guardar el ajuste.');
    }
  };

  const sortedHistory = [...history].sort((a, b) =>
    b.effective_date.localeCompare(a.effective_date)
  );

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-out duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-[2px]" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-[0.96]"
              enterTo="opacity-100 scale-100"
              leave="ease-out duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-[0.98]"
            >
              <Dialog.Panel className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-elevate-lg">
                <div className="mb-5 flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <Dialog.Title className="text-lg font-semibold text-gappex-secondary">
                      {isEditing ? 'Editar ajuste de revenue' : 'Revenue manual'}
                    </Dialog.Title>
                    <p className="mt-0.5 text-xs text-slate-400">
                      {planName} · el ajuste pisa el cálculo por edad del mes al que pertenece la fecha.
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    iconOnly
                    onClick={onClose}
                    aria-label="Cerrar"
                    className="-me-2 -mt-1"
                    icon={<CloseIcon />}
                  />
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className={fieldLabel} htmlFor="mr-date">
                        Fecha efectiva
                      </label>
                      <input
                        id="mr-date"
                        type="date"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        required
                        className={cn(field, 'tabular-nums')}
                      />
                    </div>
                    <div>
                      <label className={fieldLabel} htmlFor="mr-amount">
                        Monto mensual (ARS)
                      </label>
                      <input
                        id="mr-amount"
                        type="number"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        min={0}
                        step={0.01}
                        required
                        placeholder="Ej: 8080"
                        className={cn(field, 'tabular-nums')}
                      />
                    </div>
                  </div>

                  <div>
                    <label className={fieldLabel} htmlFor="mr-notes">
                      Notas <span className="font-normal text-slate-400">(opcional)</span>
                    </label>
                    <textarea
                      id="mr-notes"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={2}
                      placeholder="Por qué se ajusta…"
                      className={cn(field, 'resize-none')}
                    />
                  </div>

                  <AnimatePresence initial={false}>
                    {error && (
                      <Alert key="mr-error" tone="error" onClose={() => setError(null)}>
                        {error}
                      </Alert>
                    )}
                  </AnimatePresence>

                  <div className="flex items-center justify-end gap-2 pt-1">
                    {isEditing && (
                      <Button variant="ghost" type="button" onClick={resetForm} className="me-auto">
                        Cancelar edición
                      </Button>
                    )}
                    <Button variant="secondary" type="button" onClick={onClose}>
                      Cerrar
                    </Button>
                    <Button variant="primary" type="submit" disabled={saving}>
                      {saving ? 'Guardando…' : isEditing ? 'Actualizar' : 'Guardar'}
                    </Button>
                  </div>
                </form>

                {sortedHistory.length > 0 && (
                  <div className="mt-6">
                    <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                      Histórico de ajustes
                    </p>
                    <div className="max-h-60 overflow-y-auto rounded-xl bg-slate-50 p-1">
                      {sortedHistory.map((entry) => {
                        const isRowEditing = editingId === entry.id;
                        const isConfirming = confirmingId === entry.id;
                        return (
                          <div
                            key={entry.id}
                            className={cn(
                              'flex items-center gap-3 rounded-lg px-3 py-2 transition-colors duration-150 ease-out',
                              isRowEditing ? 'bg-white shadow-elevate' : 'hover:bg-white/70',
                            )}
                          >
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-semibold tabular-nums text-slate-700">
                                  {formatDateAR(entry.effective_date)}
                                </span>
                                {isRowEditing && <Badge tone="info">editando</Badge>}
                              </div>
                              <p className="truncate text-[11px] text-slate-400">
                                {entry.notes ? `${entry.notes} · ` : ''}por {entry.updated_by}
                              </p>
                            </div>

                            <span className="shrink-0 text-xs font-semibold tabular-nums text-gappex-primary">
                              {formatCurrency(entry.monthly_amount)}
                            </span>

                            {isConfirming ? (
                              <div className="flex shrink-0 items-center gap-1">
                                <span className="text-[11px] font-medium text-red-600">¿Borrar?</span>
                                <Button
                                  variant="danger"
                                  iconOnly
                                  type="button"
                                  onClick={() => handleDelete(entry)}
                                  aria-label={`Confirmar borrado del ajuste del ${formatDateAR(entry.effective_date)}`}
                                  title="Confirmar"
                                  icon={<CheckIcon />}
                                />
                                <Button
                                  variant="ghost"
                                  iconOnly
                                  type="button"
                                  onClick={() => setConfirmingId(null)}
                                  aria-label="Cancelar borrado"
                                  title="Cancelar"
                                  icon={<CloseIcon />}
                                />
                              </div>
                            ) : (
                              <div className="flex shrink-0 items-center gap-1">
                                <Button
                                  variant="ghost"
                                  iconOnly
                                  type="button"
                                  onClick={() => handleEdit(entry)}
                                  aria-label={`Editar el ajuste del ${formatDateAR(entry.effective_date)}`}
                                  title="Editar"
                                  icon={<PencilIcon />}
                                />
                                <Button
                                  variant="ghost"
                                  iconOnly
                                  type="button"
                                  onClick={() => setConfirmingId(entry.id)}
                                  aria-label={`Borrar el ajuste del ${formatDateAR(entry.effective_date)}`}
                                  title="Borrar"
                                  className="hover:text-red-600"
                                  icon={<TrashIcon />}
                                />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default EditRevenueModal;
